import { Component, OnInit, OnDestroy, ElementRef, HostListener, Input } from '@angular/core';
import { Router } from '@angular/router';
import { TasksDueService } from '../../services/tasks-due.service';
import { ChartData } from '../../models/tasks-due.models'; // Assuming ChartData is the structure { name, value, startDate, endDate }

// Define the expected structure of the data object for clarity
interface TaskReportsSummaryDomain {
    name: string; // Corresponds to dueCategory
    value: number; // Corresponds to taskCount
    startDate: string | null; // YYYY-MM-DD
    endDate: string | null; // YYYY-MM-DD
}

@Component({
    selector: 'app-tasks-due',
    templateUrl: './tasks-due.component.html',
    styleUrls: ['./tasks-due.component.scss'],
    standalone: false
})
export class TasksDueComponent implements OnInit, OnDestroy {
    // Input property to control filter visibility from parent component
    @Input() isFilterVisible: boolean = false;
    chartConfig = {
        view: [0, 0] as [number, number],  // Will be set by updateChartSize
        colorScheme: {
            domain: [
                '#FF0000', // Red for Past Due
                '#FFA500', // Orange for Due <= 7 Days
                '#FFFF00', // Yellow for Due <= 15 Days
                '#008000', // Green for Due <= 30 Days
                '#0000FF'  // Blue for Due > 30 Days
            ]
        },
        showXAxis: true,
        showYAxis: true,
        showGridLines: true,
        showXAxisLabel: false,
        showYAxisLabel: false,
        barPadding: 35,  // Adjusted padding for better bar spacing
        roundEdges: true,
        data: [] as TaskReportsSummaryDomain[], // Use the refined type here
        animations: true,
        showDataLabel: true,
        noBarWhenZero: true,
        yScaleMax: 160,
        xAxis: {
            tickFormattingFn: (label: string) => {
                // Keep original labels from API
                return label;
            }
        }
    };

    loading = false;

    constructor(
        private tasksDueService: TasksDueService,
        private elementRef: ElementRef,
        private router: Router
    ) { }

    ngOnInit(): void {
        this.loadData();
        this.updateChartSize();
    }

    private loadData(): void {
        this.loading = true;
        this.tasksDueService.getTasksDueData()
            .subscribe(
                // Position 1: Success handler (data: TaskReportsSummaryDomain[])
                (data: any) => { 
                    console.log('Tasks Due data received:', data);
                    this.chartConfig.data = data;
                    this.loading = false;
                },
                // Position 2: Error handler (error)
                (error) => {
                    console.error('Error loading tasks due data:', error);
                    this.loading = false;
                }
                // Position 3: Complete handler (optional)
            );
    }

    onSelect(event: any): void {
        if (!event || !event.name) return;
        console.log('Bar selected:', event);
        
        const category = event.name;

        // 1. FIND THE SELECTED DATA OBJECT
        const selectedData = this.chartConfig.data.find(d => d.name === category);

        if (!selectedData) {
            console.warn(`Could not find data object for category: ${category}`);
            return;
        }

        // 2. EXTRACT API-PROVIDED DATES
        const startDate = selectedData.startDate;
        const endDate = selectedData.endDate;
        
        // Base filters that apply to ALL categories
        let filters: any[] = [
            {
                field: 'isCompleted',
                operator: 'equals',
                value: 'No'
            },
            {
                field: "isRejected",
                operator: "equalsOrNull",
                value: "No"
            },
            {
                field: "statusCode",
                operator: "in",
                values: ["Open", "Assigned", "Accepted", "Ready to Complete", "For Approval"]
            }

        ];
        
        // 3. APPLY CATEGORY-SPECIFIC DATE FILTERS USING API DATES
        switch (category) {
            case 'Past Due':
                // Due date < current date
                // API provides startDate (e.g., '1900-01-01') and endDate (today - 1 day)
                if (startDate && endDate) {
                     filters.push({
                        field: 'taskDueDate',
                        operator: 'betweenDate',
                        value: startDate, // '1900-01-01'
                        valueTo: endDate  // Today - 1 day
                    });
                } else {
                    // Fallback to client-side logic if API dates are missing (less ideal)
                    const today = new Date();
                    const todayStr = this.formatDateForPayload(today);
                    filters.push({
                        field: 'taskDueDate',
                        operator: 'ltDate',
                        value: todayStr
                    });
                }
                break;
                
            case 'Due <= 7 Days':
            case 'Due <= 15 Days':
            case 'Due <= 30 Days':
                // These are range categories (betweenDate)
                if (startDate && endDate) {
                    filters.push({
                        field: 'taskDueDate',
                        operator: 'betweenDate',
                        value: startDate,
                        valueTo: endDate
                    });
                }
                break;
                
            case 'Due > 30 Days':
                // Due date > today+30
                // API provides startDate (today + 31 days) and endDate (null)
                if (startDate) {
                    filters.push({
                        field: 'taskDueDate',
                        operator: 'gteDate',
                        value: startDate // Use API's start date (e.g., '2026-01-04')
                    });
                }
                break;
                
            case 'No Due Date':
                // Task due date is null
                // Note: No additional filter needed - will be filtered on frontend
                // Backend doesn't support isNull operator yet
                break;
                
            default:
                console.warn('Unknown category:', category);
                return;
        }
        
        console.log('Navigating with filters:', filters);
        
        // Navigate to tasks grid with navigation state containing filters
        this.router.navigate(['/task-grid'], {
            state: {
                filters: filters,
                category: 'All Tasks',
                fromWidget: 'tasks-due',
                dueCategory: category
            }
        });
    }
    
    /**
     * Add days to a date
     */
    private addDays(date: Date, days: number): Date {
        const result = new Date(date);
        result.setDate(result.getDate() + days);
        return result;
    }
    
    /**
     * Format date to YYYY-MM-DD for API payload
     */
    private formatDateForPayload(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    @HostListener('window:resize')
    updateChartSize(): void {
        requestAnimationFrame(() => {
            const container = this.elementRef.nativeElement.closest('.card');
            if (!container) return;
            
            const width = container.offsetWidth || 900;
            // Use most of the container width, just leaving minimal padding
            const calculatedWidth = width - 30;
            // Compact height for vertical bar chart
            const chartHeight = 350;
            this.chartConfig.view = [calculatedWidth, chartHeight] as [number, number];
        });
    }

    ngOnDestroy(): void {
        // Nothing to clean up
    }
}
